import { useState, KeyboardEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router'
import { useAppDispatch, useAppSelector } from 'universal-scripts'
import { updateTitle, togglePublic } from 'src/store/slices/query'
import { useQueryCRUD } from '@hooks/useQuery'
import AvatarPlaceholder from '@components/AvatarPlaceholder/AvatarPlaceholder'
import BackButton from '@components/BackButton/BackButton'
import Toggle from '@components/Toggle/Toggle'
import Popover from '@components/Popover/Popover'

import './BuilderHeader.css'

export default function BuilderHeader() {
  const intl = useIntl()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { deleteQuery } = useQueryCRUD()
  const currentUser = useAppSelector((state) => state.user)
  const draft = useAppSelector((state) => state.query.draft)
  const title = draft.title ?? ''
  const isNewQuery = id === 'new'

  const [isEditingTitle, setIsEditingTitle] = useState(isNewQuery)
  const [tempTitle, setTempTitle] = useState(title)

  const displayUserName =
    currentUser?.company_name || currentUser?.name || 'User'

  const handleTitleClick = () => {
    setTempTitle(title)
    setIsEditingTitle(true)
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempTitle(e.target.value)
  }

  const commitTitleChange = () => {
    if (tempTitle.trim() !== '') {
      dispatch(updateTitle(tempTitle.trim()))
      setIsEditingTitle(false)
    }
  }

  const handleTitleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitTitleChange()
    } else if (e.key === 'Escape') {
      if (title.trim() !== '') {
        setIsEditingTitle(false)
        setTempTitle(title)
      }
    }
  }

  const handleDelete = async () => {
    const isConfirmed = window.confirm(
      intl.formatMessage({ id: 'builder.confirm.delete' })
    )
    if (isConfirmed) {
      try {
        await deleteQuery(id as string)
        navigate('/queries', { replace: true })
      } catch (error) {
        console.error('delete:', error)
      }
    }
  }

  return (
    <>
      <div>
        <BackButton to={`/queries/` + isNewQuery ? '' : id} />
      </div>
      <div className="query-data">
        <div className="user-data">
          <AvatarPlaceholder text={displayUserName} />
          <span className="user-name">{displayUserName}</span>
        </div>
        <div>{'/'}</div>

        {isEditingTitle ? (
          <input
            autoFocus
            required
            className="title-input"
            value={tempTitle}
            onChange={handleTitleChange}
            onBlur={commitTitleChange}
            onKeyDown={handleTitleKeyDown}
            placeholder={intl.formatMessage({
              id: 'builder.title.placeholder'
            })}
          />
        ) : (
          <h1 title={title} className="editable-title">
            <span onClick={handleTitleClick}>{title}</span>
            <Popover
              position="bottom"
              popoverClassName="popover-builder-header"
              buttonClassName="builder-options"
              triggerContent={<span className="icon icon-angle-down" />}
            >
              <button type="button" onClick={handleTitleClick}>
                <span>
                  <FormattedMessage id="builder-header.popover-title" />
                </span>
                <span className="icon icon-pen" />
              </button>
              {!isNewQuery && (
                <button type="button" onClick={handleDelete}>
                  <span>
                    <FormattedMessage id="builder-header.popover-delete" />
                  </span>
                  <span className="icon icon-bin" />
                </button>
              )}
            </Popover>
          </h1>
        )}
      </div>

      <div className="privacy-selectors">
        <Toggle
          checked={!!draft.public}
          onChange={() => dispatch(togglePublic())}
          leftLabel={intl.formatMessage({ id: 'privacy.private' })}
          rightLabel={intl.formatMessage({ id: 'privacy.public' })}
          id="query-privacy-toggle"
        />
      </div>
    </>
  )
}
